/**
 * config/defaults.ts
 * System default settings for the application
 * 
 * Contains realistic default values for first-time users and reset functionality.
 * Aligned with onboarding flow preferences (24h format, Mon-Fri trading days).
 * Used for:
 * - First-time user setup fallbacks
 * - "Reset to defaults" functionality in settings
 * - Base values for test data generation
 * - Development environment setup
 */

import type { BasicSettings } from '@/types/schema';
import type { LiveTradingSettings } from '@/types/schema/LiveTradingSettings';
import type { LayoutConfig } from '@/types/shared/Layout';
import { DEFAULT_PRESET_ID, DEFAULT_LAYOUT_SCHEMA_VERSION, DEFAULT_SLOT_ASSIGNMENTS } from '@/constants/layout';
import { generateUUID } from '@/utils';

// Helper type to omit database fields
type DefaultSettings<T> = Omit<T, 'id' | 'createdAt' | 'updatedAt'>;

/**
 * Basic application settings defaults
 * 
 * Aligned with onboarding flow preferences:
 * - 24-hour time format (international standard)
 * - Eastern timezone (most common US trading timezone)
 * - Monday-Friday trading days (standard work week)
 * - English language (default, others coming soon)
 */
export const DEFAULT_BASIC_SETTINGS: DefaultSettings<BasicSettings> = {
  displayTimezone: 'America/New_York',
  timeFormat: '24h',
  weekStartsOn: 'monday',
  preferredLanguage: 'en',
  tradingSessions: [
    {
      name: 'US Market Hours',
      description: 'Standard NYSE/NASDAQ trading session',
      start: '09:30',
      end: '16:00',
      isActive: true
      // daysOfWeek omitted - applies to all days
    }
  ],
  timeframeOptions: [
    { id: generateUUID(), category: 'minutes', interval: 5 },
    { id: generateUUID(), category: 'minutes', interval: 15 },
    { id: generateUUID(), category: 'hours', interval: 1 },
    { id: generateUUID(), category: 'hours', interval: 4 },
    { id: generateUUID(), category: 'days', interval: 1 },
    { id: generateUUID(), category: 'weeks', interval: 1 },
    { id: generateUUID(), category: 'months', interval: 1 },
  ],

  // Instrument Catalog & Template Sync Defaults
  instrumentCatalog: {
    updateNotifications: true,
    templateSyncPreference: 'prompt'
  }
};

/**
 * Live trading dashboard settings defaults
 *
 * Provides sensible first-use values for the trading cockpit.
 * `layoutConfig` is omitted (optional field) — use `createDefaultLayoutConfig()`
 * to generate a fresh config with a real timestamp at creation time.
 */
export const DEFAULT_LIVE_TRADING_SETTINGS: DefaultSettings<LiveTradingSettings> = {
  // Dashboard Display
  showClock: true,
  clockStyle: 'digital',
  maxPinnedObjectives: 3,
  maxPinnedSnapshotItems: 5,

  // Economic Data
  showEconomicData: true,
  economicAlertMinutes: [15, 5, 1],
  economicAlertStyle: 'subtle',

  // Note Categories — empty, user customizes
  noteCategories: [],

  // Quick Trade Buttons — disabled by default
  showQuickTrades: false,
  quickTradePresets: [],

  // Break/Pause Protocols
  breakButtonLabel: 'RESET',
  breakProtocols: [],

  // Clock Alarms — empty, user configures
  alarmRules: [],
  alarmAutoDismissMs: 15000,

  // Active Trades Display
  activeTradesView: 'cards',
  activeTradesDefaultSort: 'newest',
  activeTradesCardInfo: [
    { field: 'playbook', show: true },
    { field: 'criteria', show: true },
    { field: 'progress', show: true },
    { field: 'emotions', show: true },
    { field: 'modified', show: true },
  ],
};

/**
 * Create a fresh default layout configuration with a real timestamp
 *
 * Factory function matching the `createDefaultMetricsForSource()` pattern.
 * Generates a new `LayoutConfig` each time it's called, with `workingLayout.updatedAt`
 * set to the current time. Used by both new-record creation and migration backfill.
 *
 * @returns Fresh LayoutConfig with default preset and real ISO timestamp
 */
export function createDefaultLayoutConfig(): LayoutConfig {
  return {
    schemaVersion: DEFAULT_LAYOUT_SCHEMA_VERSION,
    activePresetId: DEFAULT_PRESET_ID,
    workingLayout: {
      presetId: DEFAULT_PRESET_ID,
      bannerConfig: { visible: true, position: 'top' },
      slotAssignments: { ...DEFAULT_SLOT_ASSIGNMENTS },
      updatedAt: new Date().toISOString(),
    },
    savedLayouts: [],
    autosaveEnabled: true,
  };
}

// TODO: Add other settings defaults as UI components are built:
// export const DEFAULT_PRE_TRADING_SETTINGS: DefaultSettings<PreTradingSettings>
// export const DEFAULT_POST_TRADING_SETTINGS: DefaultSettings<PostTradingSettings>
// export const DEFAULT_OPPORTUNITY_SETTINGS: DefaultSettings<OpportunitySettings>

// ============================================================================
// Health Metrics Defaults
// ============================================================================

/**
 * Health metric source type
 */
export type HealthMetricSource = 'apple' | 'whoop' | 'oura' | 'garmin' | 'custom';

/**
 * Health metric template (without id - generated at apply time)
 */
type HealthMetricTemplate = {
  name: string;
  min: number;
  max: number;
  unit?: string;
};

/**
 * Default health metric templates by source
 * IDs are NOT included - use createDefaultMetricsForSource() to generate fresh UUIDs
 */
const DEFAULT_HEALTH_METRIC_TEMPLATES: Record<HealthMetricSource, HealthMetricTemplate[]> = {
  apple: [
    { name: 'HRV', min: 0, max: 150, unit: 'ms' },
    { name: 'Resting HR', min: 40, max: 100, unit: 'bpm' },
    { name: 'Sleep', min: 0, max: 12, unit: 'hrs' },
    { name: 'Activity', min: 0, max: 100, unit: '%' },
  ],
  whoop: [
    { name: 'Strain', min: 0, max: 21 },
    { name: 'Recovery', min: 0, max: 100, unit: '%' },
    { name: 'HRV', min: 0, max: 150, unit: 'ms' },
    { name: 'Sleep Performance', min: 0, max: 100, unit: '%' },
  ],
  oura: [
    { name: 'Readiness', min: 0, max: 100 },
    { name: 'Sleep Score', min: 0, max: 100 },
    { name: 'HRV', min: 0, max: 150, unit: 'ms' },
    { name: 'Activity', min: 0, max: 100 },
  ],
  garmin: [
    { name: 'Body Battery', min: 0, max: 100, unit: '%' },
    { name: 'Stress', min: 0, max: 100, unit: '%' },
    { name: 'Sleep Score', min: 0, max: 100, unit: '%' },
    { name: 'HRV', min: 0, max: 150, unit: 'ms' },
  ],
  custom: [], // Empty - user configures manually
};

/**
 * Health metric with id (for use in components)
 */
export interface HealthMetricWithId {
  id: string;
  name: string;
  min: number;
  max: number;
  unit?: string;
}

/**
 * Create default metrics for a source with fresh UUIDs
 * Use this when applying defaults - generates new IDs each time
 *
 * @param source - The health metric source
 * @returns Array of health metrics with generated UUIDs
 */
export function createDefaultMetricsForSource(source: HealthMetricSource): HealthMetricWithId[] {
  const templates = DEFAULT_HEALTH_METRIC_TEMPLATES[source];
  return templates.map((template) => ({
    ...template,
    id: generateUUID(),
  }));
}